/**
 * Typed API client. Every server call in the web app goes through here (Phase 2
 * seam for the Tauri shell: swap the base URL / token source, nothing else).
 *
 * Session: Cortex's own JWT, claimed from POST /api/hub/session after a POST /hub-auth launch
 * or POST /api/auth/dev-login, stored in localStorage as in the other CQTex modules.
 */
import type {
  Backlink, GraphEdge, GraphPayload, HubClientConfig, HubHealth, NoteCreateInput, NoteDetail, NoteListQuery, NoteSummary, NoteTypeSchemaDef,
  SearchResponse, SessionUser, UnlinkedMention, VaultTreeNode, ModuleRole,
  AgentSessionSummary, AgentSessionDetail, SessionCreateInput, SessionMessageInput, ProviderStatus, BudgetStatus, UsageReport, ProviderId, ToolCallRecord, ProposalRecord, ProposalStatus, RiskLevel, AgentMode,
  CredentialSummary, CredentialCreateInput, CredentialUpdateInput, VaultStatus as CredentialVaultStatus, AuditQuery,
  CaseSummary, CaseDetail, CaseCreateInput, CaseStepInput, CaseCloseInput, CasePatchInput, CaseStepRecord, RunbookRecord, RunbookMatch,
  InsightRecord, InsightKind, InsightStatus, JobRecord, ScheduleEntry, WatcherRecord, WatcherCreateInput, WatcherRule, WorkerKind,
  IngestResult, IngestCommitInput, IngestCommitResult, VoiceConfig, TranscriptResult,
} from '@cortex/shared';

const TOKEN_KEY = 'cortex.token';

export class ApiError extends Error {
  constructor(public status: number, message: string, public body: Record<string, unknown> = {}) {
    super(message);
  }
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

function headers(extra: Record<string, string> = {}): Record<string, string> {
  const h: Record<string, string> = { ...extra };
  const t = getToken();
  if (t) h.authorization = `Bearer ${t}`;
  return h;
}

async function call<T>(method: string, path: string, body?: unknown, init: RequestInit = {}): Promise<T> {
  const isRaw = body instanceof ArrayBuffer || body instanceof Blob;
  const res = await fetch(path, {
    method,
    ...init,
    headers: headers({ ...(body !== undefined && !isRaw ? { 'content-type': 'application/json' } : {}), ...((init.headers as Record<string, string>) ?? {}) }),
    body: body === undefined ? undefined : isRaw ? (body as BodyInit) : JSON.stringify(body),
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { error: text };
  }
  if (!res.ok) {
    const b = (data ?? {}) as Record<string, unknown>;
    if (res.status === 401 && getToken() && !path.startsWith('/api/auth/') && !path.startsWith('/api/hub/')) {
      // Session expired or revoked: drop it and let the app show the launch page.
      clearToken();
      window.dispatchEvent(new CustomEvent('cortex:session-lost'));
    }
    throw new ApiError(res.status, typeof b.error === 'string' ? b.error : `${res.status} ${res.statusText}`, b);
  }
  return data as T;
}

const qs = (o: Record<string, unknown>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
};

export interface TitleHit {
  id: string;
  path: string;
  title: string;
  type: string;
  alias?: string;
}

export interface VaultStatus {
  vaultPath: string;
  notes: number;
  softDeleted: number;
  edges: number;
  unresolvedEdges: number;
  attachments: number;
  ftsDocs: number;
  lastFullReindex: string | null;
  lastScan: string | null;
  reindexing: boolean;
  watcher: { running: boolean; ready: boolean; lastEventAt: string | null; pending: number; processed: number; errors: number; lastError: string | null };
  git: boolean;
  embed: EmbedStatus | null;
}

export interface EmbedStatus {
  embedder: string | null;
  vectorStore: string;
  healthy: boolean;
  healthDetail: string;
  running: boolean;
  pending: number;
  done: number;
  total: number;
  skippedRestricted: number;
  ratePerMin: number;
  lastError: string | null;
  lastBatchAt: string | null;
}
export interface SearchStatus {
  embed: EmbedStatus;
  hybrid: { ok: boolean; reason: string | null };
  localOnly: boolean;
}
export interface SavedSearch {
  slug: string;
  name: string;
  query: string;
  type?: string;
  tag?: string;
  folder?: string;
  mode: 'hybrid' | 'keyword';
}
export interface EntityCard {
  id: string;
  path: string;
  title: string;
  kind: string;
  fields: Record<string, unknown>;
  tags: string[];
  mtime: string;
}
export interface EntityResolve {
  query: string;
  candidates: Array<EntityCard & { matchedOn: string }>;
  ambiguous: boolean;
}
export interface EntityDetail {
  entity: EntityCard;
  neighbours: Array<EntityCard & { via: string; direction: 'in' | 'out' }>;
  cases: Array<{ id: string; path: string; title: string; status: string | null; opened: string | null }>;
  related: Array<{ id: string; path: string; title: string; type: string }>;
}

export interface DriftRow {
  id: string;
  path: string;
  title: string;
  type: string;
  schemaWarnings: Array<{ path: string; message: string }>;
}

export interface ToolInfo {
  name: string;
  description: string;
  riskLevel: RiskLevel;
  modes: AgentMode[] | null;
  policy: { enabled: boolean; minRole: ModuleRole; requiresApproval: boolean; targetAllow: string[] | null; targetDeny: string[] | null; rateLimit: { perMinute?: number; perSession?: number } | null; timeoutMs: number };
  schema: { type: 'object'; properties?: Record<string, JsonSchemaProp>; required?: string[] } | null;
}
export interface JsonSchemaProp {
  type?: string | string[];
  description?: string;
  enum?: unknown[];
  default?: unknown;
  items?: JsonSchemaProp;
  minimum?: number;
  maximum?: number;
  anyOf?: JsonSchemaProp[];
}
export type InvokeResult =
  | { status: 'OK'; record: ToolCallRecord; output: unknown }
  | { status: 'AWAITING_APPROVAL'; record: ToolCallRecord }
  | { status: 'DENIED' | 'FAILED'; record: ToolCallRecord; error: string };

export const Api = {
  auth: {
    me: () => call<SessionUser>('GET', '/api/auth/me'),
    devLogin: (body: { email?: string; name?: string; role: ModuleRole }) => call<{ token: string; user: SessionUser }>('POST', '/api/auth/dev-login', body),
  },
  hub: {
    config: () => call<HubClientConfig>('GET', '/api/hub/config'),
    /** Claim the session a POST /hub-auth launch left behind its one-time HttpOnly cookie. */
    session: () => call<{ token: string; user: SessionUser }>('POST', '/api/hub/session'),
    health: () => call<HubHealth>('GET', '/api/hub/health'),
  },
  notes: {
    list: (q: Partial<NoteListQuery>) => call<{ items: NoteSummary[]; total: number }>('GET', `/api/notes${qs(q)}`),
    titles: (q: string, limit = 20) => call<TitleHit[]>('GET', `/api/notes/titles${qs({ q, limit })}`),
    get: (id: string) => call<NoteDetail>('GET', `/api/notes/${encodeURIComponent(id)}`),
    byPath: (path: string) => call<NoteDetail>('GET', `/api/notes/by-path${qs({ path })}`),
    create: (input: NoteCreateInput) => call<NoteDetail>('POST', '/api/notes', input),
    update: (id: string, raw: string, baseHash: string) => call<NoteDetail>('PUT', `/api/notes/${encodeURIComponent(id)}`, { raw, baseHash }),
    rename: (id: string, newPath: string) => call<NoteDetail>('POST', `/api/notes/${encodeURIComponent(id)}/rename`, { newPath }),
    remove: (id: string) => call<{ path: string; trashedTo: string }>('DELETE', `/api/notes/${encodeURIComponent(id)}`),
    backlinks: (id: string) => call<Backlink[]>('GET', `/api/notes/${encodeURIComponent(id)}/backlinks`),
    links: (id: string) => call<GraphEdge[]>('GET', `/api/notes/${encodeURIComponent(id)}/links`),
    unlinked: (id: string) => call<UnlinkedMention[]>('GET', `/api/notes/${encodeURIComponent(id)}/unlinked-mentions`),
    history: (id: string) => call<Array<{ hash: string; date: string; message: string; author: string }>>('GET', `/api/notes/${encodeURIComponent(id)}/history`),
  },
  search: (q: string, opts: { limit?: number; offset?: number; prefix?: boolean; type?: string; tag?: string; folder?: string; mode?: 'hybrid' | 'keyword' } = {}) =>
    call<SearchResponse>('GET', `/api/search${qs({ q, ...opts })}`),
  searchFacets: (q: string) => call<{ type: Record<string, number>; tag: Record<string, number>; folder: Record<string, number> }>('GET', `/api/search/facets${qs({ q })}`),
  searchStatus: () => call<SearchStatus>('GET', '/api/search/status'),
  savedSearches: {
    list: () => call<SavedSearch[]>('GET', '/api/search/saved'),
    save: (s: Omit<SavedSearch, 'slug'>) => call<SavedSearch>('POST', '/api/search/saved', s),
    remove: (slug: string) => call<void>('DELETE', `/api/search/saved/${encodeURIComponent(slug)}`),
  },
  entities: {
    resolve: (q: string) => call<EntityResolve>('GET', `/api/entities/resolve${qs({ q })}`),
    get: (id: string) => call<EntityDetail>('GET', `/api/entities/${encodeURIComponent(id)}`),
    graph: () => call<GraphPayload & { clusters: Record<string, string> }>('GET', '/api/entities/graph'),
  },
  graph: {
    global: (opts: { type?: string; includeUnresolved?: boolean } = {}) => call<GraphPayload>('GET', `/api/graph${qs(opts)}`),
    local: (id: string, hops = 1, limit = 300) => call<GraphPayload>('GET', `/api/graph/local/${encodeURIComponent(id)}${qs({ hops, limit })}`),
    layout: (view: 'global' | 'entity') => call<{ positions: Record<string, [number, number]>; view: string; savedAt: string | null }>('GET', `/api/graph/layout${qs({ view })}`),
    saveLayout: (view: 'global' | 'entity', positions: Record<string, [number, number]>) => call<{ ok: boolean; nodes: number }>('PUT', '/api/graph/layout', { view, positions }),
  },
  vault: {
    tree: () => call<VaultTreeNode[]>('GET', '/api/vault/tree'),
    status: () => call<VaultStatus>('GET', '/api/vault/status'),
    reindex: (full = false) => call<Record<string, number>>('POST', `/api/vault/reindex${qs({ full })}`),
    unresolved: () => call<Array<{ target: string; count: number; fromPaths: string[] }>>('GET', '/api/vault/unresolved'),
  },
  types: {
    list: () => call<NoteTypeSchemaDef[]>('GET', '/api/types'),
    save: (key: string, def: Partial<NoteTypeSchemaDef>) => call<NoteTypeSchemaDef>('PUT', `/api/types/${encodeURIComponent(key)}`, def),
    remove: (key: string) => call<void>('DELETE', `/api/types/${encodeURIComponent(key)}`),
    drift: () => call<DriftRow[]>('GET', '/api/types/drift'),
  },
  attachments: {
    upload: (name: string, blob: Blob, note?: string) =>
      call<{ path: string; bytes: number; mime: string }>('POST', `/api/attachments${qs({ name, note })}`, blob, {
        headers: { 'content-type': blob.type || 'application/octet-stream' },
      }),
    url: (path: string) => `/api/attachments/file${qs({ path })}`,
  },
  agent: {
    providers: () => call<{ providers: ProviderStatus[]; localOnly: boolean; default: ProviderId | null; budget?: BudgetStatus }>('GET', '/api/agent/providers'),
    usage: (o: { months?: number; days?: number } = {}) => call<UsageReport>('GET', `/api/agent/usage${qs(o)}`),
    tools: () => call<{ tools: ToolInfo[] }>('GET', '/api/agent/tools'),
    invoke: (name: string, input: Record<string, unknown>) =>
      call<InvokeResult>('POST', '/api/agent/tools/invoke', { name, input }).catch((e: ApiError) => {
        if (e.body && typeof e.body.status === 'string' && e.body.record) return e.body as unknown as InvokeResult;
        throw e;
      }),
    /** Approve a palette call the server parked. The input that runs is the one it recorded, not ours. */
    approveCall: (callId: string) =>
      call<InvokeResult>('POST', `/api/agent/tools/calls/${encodeURIComponent(callId)}/approve`).catch((e: ApiError) => {
        if (e.body && typeof e.body.status === 'string' && e.body.record) return e.body as unknown as InvokeResult;
        throw e;
      }),
    denyCall: (callId: string) => call<ToolCallRecord>('POST', `/api/agent/tools/calls/${encodeURIComponent(callId)}/deny`),
    setPolicy: (name: string, patch: Partial<ToolInfo['policy']>) => call<{ policy: ToolInfo['policy'] }>('PUT', `/api/agent/tools/${encodeURIComponent(name)}/policy`, patch),
    sessions: () => call<{ sessions: AgentSessionSummary[] }>('GET', '/api/agent/sessions'),
    create: (input: Partial<SessionCreateInput>) => call<AgentSessionSummary>('POST', '/api/agent/sessions', input),
    get: (id: string) => call<AgentSessionDetail>('GET', `/api/agent/sessions/${encodeURIComponent(id)}`),
    patch: (id: string, p: { mode?: AgentMode; provider?: ProviderId | null; allowRestricted?: boolean; pinned?: string[]; title?: string }) => call<AgentSessionSummary>('PATCH', `/api/agent/sessions/${encodeURIComponent(id)}`, p),
    end: (id: string) => call<void>('DELETE', `/api/agent/sessions/${encodeURIComponent(id)}`),
    send: (id: string, input: SessionMessageInput) => call<{ status: string }>('POST', `/api/agent/sessions/${encodeURIComponent(id)}/messages`, input),
    cancel: (id: string) => call<{ cancelled: boolean }>('POST', `/api/agent/sessions/${encodeURIComponent(id)}/cancel`),
    approve: (id: string, callId: string) => call<ToolCallRecord>('POST', `/api/agent/sessions/${encodeURIComponent(id)}/calls/${encodeURIComponent(callId)}/approve`),
    deny: (id: string, callId: string) => call<ToolCallRecord>('POST', `/api/agent/sessions/${encodeURIComponent(id)}/calls/${encodeURIComponent(callId)}/deny`),
    streamUrl: (id: string, since = 0) => {
      const t = getToken();
      return `/api/agent/sessions/${encodeURIComponent(id)}/stream${qs({ since, access_token: t })}`;
    },
    proposals: (status?: ProposalStatus) => call<{ proposals: ProposalRecord[]; pending: number }>('GET', `/api/agent/proposals${qs({ status })}`),
    proposal: (id: string) => call<ProposalRecord & { stale: boolean; currentHash: string | null }>('GET', `/api/agent/proposals/${encodeURIComponent(id)}`),
    acceptProposal: (id: string, hunks?: number[]) => call<{ proposal: ProposalRecord; noteId: string; path: string }>('POST', `/api/agent/proposals/${encodeURIComponent(id)}/accept`, { hunks }),
    rejectProposal: (id: string) => call<ProposalRecord>('POST', `/api/agent/proposals/${encodeURIComponent(id)}/reject`),
  },
  credentials: {
    list: () => call<{ vault: CredentialVaultStatus; credentials: CredentialSummary[] }>('GET', '/api/credentials'),
    unused: (days = 90) => call<{ days: number; credentials: CredentialSummary[] }>('GET', `/api/credentials/unused${qs({ days })}`),
    create: (input: CredentialCreateInput) => call<CredentialSummary>('POST', '/api/credentials', input),
    update: (id: string, input: CredentialUpdateInput) => call<CredentialSummary>('PUT', `/api/credentials/${encodeURIComponent(id)}`, input),
    remove: (id: string) => call<void>('DELETE', `/api/credentials/${encodeURIComponent(id)}`),
  },
  admin: {
    audit: (q: Partial<AuditQuery>) =>
      call<{ entries: Array<{ id: string; at: string; actor: string; action: string; target: string | null; user: { email: string; name: string } | null; detail: Record<string, unknown>; ip: string | null }>; nextBefore: string | null; actions: Array<{ action: string; count: number }> }>('GET', `/api/admin/audit${qs(q)}`),
    calls: (q: { tool?: string; status?: string; limit?: number } = {}) => call<{ calls: Array<ToolCallRecord & { userId: string | null }> }>('GET', `/api/admin/calls${qs(q)}`),
    users: () => call<{ users: Array<{ id: string; email: string; name: string; role: ModuleRole; isActive: boolean; hubSub: string | null; lastSeenAt: string; createdAt: string }> }>('GET', '/api/admin/users'),
    updateUser: (id: string, p: { isActive?: boolean; role?: ModuleRole }) => call<{ id: string; role: ModuleRole; isActive: boolean }>('PUT', `/api/admin/users/${encodeURIComponent(id)}`, p),
  },
  cases: {
    list: (q: { status?: string; open?: boolean; site?: string; q?: string; limit?: number } = {}) => call<{ cases: CaseSummary[]; open: number }>('GET', `/api/cases${qs(q)}`),
    get: (id: string) => call<CaseDetail>('GET', `/api/cases/${encodeURIComponent(id)}`),
    create: (input: Partial<CaseCreateInput> & { title: string; symptom: string }) => call<CaseDetail>('POST', '/api/cases', input),
    patch: (id: string, p: CasePatchInput) => call<CaseDetail>('PATCH', `/api/cases/${encodeURIComponent(id)}`, p),
    addStep: (id: string, s: CaseStepInput) => call<CaseStepRecord>('POST', `/api/cases/${encodeURIComponent(id)}/steps`, s),
    close: (id: string, p: CaseCloseInput) => call<CaseDetail>('POST', `/api/cases/${encodeURIComponent(id)}/close`, p),
    /** Replace a hand-edited case note with the agent's rendering. The old version stays in the vault's git history. */
    resync: (id: string) => call<CaseDetail>('POST', `/api/cases/${encodeURIComponent(id)}/resync`),
  },
  runbooks: {
    list: () => call<{ runbooks: RunbookRecord[] }>('GET', '/api/runbooks'),
    match: (q: string, entities: string[] = []) => call<{ matches: RunbookMatch[] }>('GET', `/api/runbooks/match${qs({ q, entities: entities.join(',') || undefined })}`),
  },
  insights: {
    list: (q: { status?: InsightStatus; kind?: InsightKind; limit?: number } = {}) => call<{ insights: InsightRecord[]; counts: { new: number; byKind: Record<string, number> } }>('GET', `/api/insights${qs(q)}`),
    counts: () => call<{ new: number; byKind: Record<string, number> }>('GET', '/api/insights/counts'),
    accept: (id: string) => call<{ insight: InsightRecord; applied: { noteId: string; path: string } | null }>('POST', `/api/insights/${encodeURIComponent(id)}/accept`),
    dismiss: (id: string) => call<InsightRecord>('POST', `/api/insights/${encodeURIComponent(id)}/dismiss`),
  },
  watchers: {
    list: () => call<{ watchers: WatcherRecord[]; max: number }>('GET', '/api/watchers'),
    create: (input: Partial<WatcherCreateInput> & { name: string; rule: WatcherRule }) => call<WatcherRecord>('POST', '/api/watchers', input),
    patch: (id: string, p: { name?: string; cron?: string; rule?: WatcherRule; enabled?: boolean }) => call<WatcherRecord>('PATCH', `/api/watchers/${encodeURIComponent(id)}`, p),
    remove: (id: string) => call<void>('DELETE', `/api/watchers/${encodeURIComponent(id)}`),
    run: (id: string) => call<JobRecord>('POST', `/api/watchers/${encodeURIComponent(id)}/run`),
  },
  jobs: {
    list: (kind?: WorkerKind) => call<{ schedule: ScheduleEntry[]; jobs: JobRecord[]; known: WorkerKind[] }>('GET', `/api/jobs${qs({ kind })}`),
    run: (kind: string) => call<JobRecord>('POST', `/api/jobs/run/${encodeURIComponent(kind)}`),
    setEnabled: (kind: string, enabled: boolean) => call<{ kind: string; enabled: boolean }>('PUT', `/api/jobs/${encodeURIComponent(kind)}`, { enabled }),
    briefs: () => call<{ briefs: Array<{ id: string; topic: string; cron: string; enabled: boolean; ownerId: string; lastRunAt: string | null; lastError: string | null }> }>('GET', '/api/jobs/briefs'),
    createBrief: (b: { topic: string; cron: string; enabled: boolean }) => call<{ id: string }>('POST', '/api/jobs/briefs', b),
    runBrief: (id: string) => call<JobRecord>('POST', `/api/jobs/briefs/${encodeURIComponent(id)}/run`),
    removeBrief: (id: string) => call<void>('DELETE', `/api/jobs/briefs/${encodeURIComponent(id)}`),
  },
  ingest: {
    formats: () => call<{ formats: Array<{ kind: string; extensions: string[] }>; ocr: boolean; urls: boolean }>('GET', '/api/ingest/formats'),
    upload: (file: File) => call<{ results: IngestResult[] }>('POST', `/api/ingest${qs({ name: file.name })}`, file, { headers: { 'content-type': file.type || 'application/octet-stream' } }),
    url: (url: string) => call<{ results: IngestResult[] }>('POST', '/api/ingest/url', { url }),
    commit: (input: IngestCommitInput) => call<IngestCommitResult>('POST', '/api/ingest/commit', input),
  },
  voice: {
    config: () => call<VoiceConfig>('GET', '/api/voice/config'),
    transcribe: (audio: Blob) => call<TranscriptResult>('POST', '/api/voice/transcribe', audio, { headers: { 'content-type': audio.type || 'audio/webm' } }),
    speak: async (text: string): Promise<Blob> => {
      const res = await fetch('/api/voice/speak', { method: 'POST', headers: headers({ 'content-type': 'application/json' }), body: JSON.stringify({ text }) });
      if (!res.ok) throw new ApiError(res.status, `speak ${res.status}`);
      return res.blob();
    },
  },
  eventsUrl: () => {
    const t = getToken();
    return `/api/vault/events${t ? qs({ access_token: t }) : ''}`;
  },
};
